<script setup lang="ts">
/** A rule, or a measured gap. */
import { useI18n } from 'vue-i18n'

import InspectorField from '@/sections/InspectorField.vue'
import type { DividerSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: DividerSection }>()
const emit = defineEmits<{ change: [section: DividerSection] }>()

const VARIANTS = ['rule', 'space'] as const

function set(changes: Partial<DividerSection>): void {
  emit('change', { ...props.section, ...changes })
}
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.divider.variant')">
      <div class="variants" role="group" :aria-label="t('inspector.divider.variant')">
        <button
          v-for="variant in VARIANTS"
          :key="variant"
          type="button"
          class="variants__option"
          :class="{ 'variants__option--on': section.variant === variant }"
          :aria-pressed="section.variant === variant"
          @click="set({ variant })"
        >
          {{ t(`inspector.divider.variants.${variant}`) }}
        </button>
      </div>
    </InspectorField>

    <InspectorField
      v-if="section.variant === 'space'"
      :label="t('inspector.divider.space')"
      :hint="t('inspector.divider.spaceHint', { mm: section.space })"
    >
      <input
        class="range"
        type="range"
        min="4"
        max="60"
        step="2"
        :value="section.space"
        @input="set({ space: Number(($event.target as HTMLInputElement).value) })"
      />
    </InspectorField>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.range {
  width: 100%;
  accent-color: var(--accent);
}

.variants {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  padding: 2px;
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.variants__option {
  height: 26px;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.variants__option--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}
</style>
